import { Editor, Node, Path, Text, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { ChipElement, CustomText } from '../types';
import { isChipLike, isChipPattern, selectAfterNode } from './utils';
import { OPERATOR_TAKES_PARENTHESES } from '../constants';
import { parseExpression } from '../utils';

export const chipHandlers = {
  // [PURPOSE]: Handles the conversion of a chip element marked with 'editChip' into an editable text node, allowing the user to modify the chip content.
  convertChipToText(editor: Editor, node: Node, path: Path) {
    if (!(isChipLike(node) && (node as any).editChip === true)) {
      return false;
    }

    if (!Editor.hasPath(editor, path)) return false;

    Editor.withoutNormalizing(editor, () => {
      // Get the chip's components (use type assertion for chip-like nodes)
      const chipNode = node as ChipElement;

      // Reconstruct the text from chip components to avoid extra spaces
      const parts = [
        chipNode.prefixOperator,
        chipNode.field,
        chipNode.operator,
        chipNode.value,
      ].filter(Boolean);
      const chipText = parts.join(' ');

      // Create text node marked as editing (prevents normalization)
      const editableTextNode = {
        isEditing: true,
        text: chipText,
      } as CustomText;

      // Get parent path and index for insertion
      const parentPath = Path.parent(path);
      const index = path.at(-1) ?? 0;

      // Remove the chip and any trailing space node
      Transforms.removeNodes(editor, { at: path });

      // Check if next node is just a space and remove it too
      const nextPath = [...parentPath, index];
      if (Editor.hasPath(editor, nextPath)) {
        try {
          const [nextNode] = Editor.node(editor, nextPath);
          if (Text.isText(nextNode) && nextNode.text === ' ') {
            Transforms.removeNodes(editor, { at: nextPath });
          }
        } catch {
          // Node doesn't exist or can't be accessed
        }
      }

      // Insert editable text node
      const insertPath = [...parentPath, index];
      Transforms.insertNodes(editor, editableTextNode, {
        at: insertPath,
      });

      // Position cursor at END of text (per user requirement)
      const textLength = chipText.length;
      Transforms.select(editor, {
        offset: textLength,
        path: insertPath,
      });

      // Focus the editor
      try {
        ReactEditor.focus(editor);
      } catch {
        // Handle silently
      }
    });

    return true;
  },
  // [PURPOSE]: Handles the conversion of a text node marked with 'convertToChip' into a 'chip' element, parsing out field, operator, and value, and handling special cases like parenthesized values for 'IN'/'NOT IN'.
  convertToChip(editor: Editor, node: Node, path: Path) {
    if (!(Text.isText(node) && (node as CustomText).convertToChip)) {
      return false;
    }

    if (!Editor.hasPath(editor, path)) return false;

    const [[prefixOperator, field, operator, value = '']] = parseExpression(node.text);

    // Special handling for IN operator with parenthesized values
    const isInOpWithParens =
      operator &&
      OPERATOR_TAKES_PARENTHESES[operator as keyof typeof OPERATOR_TAKES_PARENTHESES] &&
      value.trim().startsWith('(');

    // Check if there's a closing parenthesis in the node text
    const closingParenIndex = node.text.indexOf(')');
    const hasParenInCurrentNode = closingParenIndex !== -1;

    // Handle parentheses in value differently for IN operator
    let cleanValue = value;
    let hasParen = false;

    if (isInOpWithParens) {
      if (hasParenInCurrentNode) {
        const startIndex = node.text.indexOf(value);
        const endIndex = closingParenIndex + 1;

        const rawValueWithParen = node.text.slice(startIndex, endIndex).trim();
        // Remove trailing comma + optional space *before* the closing parenthesis
        cleanValue = rawValueWithParen.replace(/,\s*\)$/, ')');
        hasParen = false; // We've already included the closing paren in value
      } else {
        // No closing paren in this node, keep value as is
        cleanValue = value;
        hasParen = false; // Don't add extra paren after chip
      }
    }

    // Wrap value in parentheses for IN/NOT IN if not already present
    let finalValue = cleanValue;
    if (
      operator &&
      OPERATOR_TAKES_PARENTHESES[operator as keyof typeof OPERATOR_TAKES_PARENTHESES] &&
      !finalValue.trim().startsWith('(')
    ) {
      finalValue = `(${finalValue.trim()})`;
    }

    Editor.withoutNormalizing(editor, () => {
      // Create the chip
      const chipNode = {
        children: [
          { text: [prefixOperator, field, operator, finalValue].filter(Boolean).join(' ') },
        ],
        field,
        operator,
        prefixOperator,
        type: 'chip',
        value: finalValue,
      };

      // Create text node with paren if needed
      const textAfterNode = hasParen ? { text: ') ' } : { text: ' ' };

      // Replace the node with the chip and text
      Transforms.removeNodes(editor, { at: path });
      Transforms.insertNodes(editor, [chipNode as ChipElement, textAfterNode as CustomText], {
        at: path,
      });

      // Select after the chip
      selectAfterNode(editor, path);
    });

    return true;
  },

  // [PURPOSE]: Handles the deletion of a chip element that has been marked with 'deleteChip: true', replacing it with an empty text node.
  deleteChip(editor: Editor, node: Node, path: Path) {
    // Check if the node is a chip marked for deletion
    if (!(isChipLike(node) && (node as any).deleteChip === true)) {
      return false; // Not the node we're looking for
    }

    // Perform the deletion and insertion in one go
    Editor.withoutNormalizing(editor, () => {
      try {
        // Get the parent path and index before removing the node
        const parentPath = Path.parent(path);
        const index = path.at(-1) ?? 0;

        Transforms.removeNodes(editor, { at: path });

        // Insert an empty text node at the same position
        const emptyTextNode = { text: '' } as CustomText;
        const insertPath = [...parentPath, index];
        Transforms.insertNodes(editor, emptyTextNode, {
          at: insertPath,
          select: true, // Select the new node after insertion
        });
      } catch {
        // Error handling without console.error

        try {
          // Recovery logic
          if (Editor.node(editor, path)) {
            // Check if node still exists before unsetting
            Transforms.unsetNodes(editor, 'deleteChip', { at: path });
          }
        } catch {
          // Error handling without console.error
        }
      }
    });

    // Returning true signifies that normalization occurred for this node
    // Slate will handle the next pass.
    return true;
  },

  // [PURPOSE]: Detects if a text node matches the 'isChipPattern' criteria (field operator value) and marks it for conversion ('convertToChip: true'), considering various conditions like balanced parentheses, quotes, and trailing spaces to decide if conversion should be immediate. Handles splitting nodes if a closing parenthesis is found within the potential chip text.
  detectChipPattern(editor: Editor, node: Node, path: Path) {
    if (!(Text.isText(node) && node.text.trim() && !node.convertToChip && !node.isEditing)) {
      return false;
    }

    const pattern = isChipPattern(node.text);
    if (pattern) {
      Transforms.setNodes(editor, { convertToChip: true }, { at: path });

      return true;
    }

    return false;
  },
};

export default chipHandlers;
